import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, BookOpen, ExternalLink, Sparkles, ShoppingCart, Store } from "lucide-react";
import api from "@/lib/api";
import { getAvailableStoresForBook } from "@/lib/affiliates";
import { useCountry } from "@/contexts/CountryContext";

// Icon registry shared with BookCard. Casa del Libro / Amazon / Fnac /
// El Corte Inglés / CEGAL all surface as small lucide icons inside the
// store buttons.
const ICONS = {
    ShoppingCart,
    BookOpen,
    Store,
    ExternalLink,
};

export default function LibroPage() {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { country } = useCountry();

    const q = (searchParams.get("q") || "").trim();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!q) {
            setError("missing_q");
            setLoading(false);
            return;
        }
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/books/details", { params: { q } });
                if (alive) setData(data);
            } catch (e) {
                if (alive) setError(e.response?.data?.detail || "error");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [q]);

    if (loading) {
        return (
            <main className="min-h-[calc(100vh-73px)] flex items-center justify-center">
                <p className="font-display text-2xl italic text-[var(--ink-muted)] animate-slow-pulse">
                    {t("libro.loading")}
                </p>
            </main>
        );
    }

    if (error === "missing_q" || !data) {
        return (
            <main className="min-h-[calc(100vh-73px)] flex flex-col items-center justify-center text-center px-6">
                <p
                    className="font-display text-3xl text-[var(--ink)] mb-3 italic"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 64' }}
                >
                    {t("libro.not_found")}
                </p>
                <Link
                    to="/"
                    className="mt-4 inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-5 py-2.5 text-xs uppercase tracking-widest"
                >
                    <ArrowLeft size={13} strokeWidth={1.6} />
                    {t("libro.back_home")}
                </Link>
            </main>
        );
    }

    const book = {
        title: data.title,
        author: data.author,
        isbn: data.isbn || null,
        cover_url: data.cover_url || null,
    };
    const stores = getAvailableStoresForBook(book, country);
    const chainStores = stores.filter((s) => !s.indie);
    const indieStores = stores.filter((s) => s.indie);

    const canonical = `https://recomendalibros.com/libro?q=${encodeURIComponent(q)}`;
    const pageTitle = `${data.title}${data.author ? ` — ${data.author}` : ""} · Recomenda·Libros`;
    const pageDesc =
        data.synopsis?.slice(0, 200) ||
        t("libro.meta_desc_fallback", { title: data.title, author: data.author });

    const handleSeeSimilar = () => {
        navigate(`/?book_title=${encodeURIComponent(data.title)}`);
    };

    return (
        <main
            data-testid="libro-page"
            className="min-h-[calc(100vh-73px)] max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-20"
        >
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDesc} />
                <link rel="canonical" href={canonical} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDesc} />
                {data.cover_url && <meta property="og:image" content={data.cover_url} />}
            </Helmet>

            <Link
                to={searchParams.get("from") === "blog" ? "/blog" : "/"}
                data-testid="libro-back"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-[var(--ink-muted)] hover:text-[var(--ink)] transition-colors mb-10"
            >
                <ArrowLeft size={12} strokeWidth={1.8} />
                {t("libro.back")}
            </Link>

            <div className="grid md:grid-cols-[280px_1fr] lg:grid-cols-[320px_1fr] gap-10 md:gap-14">
                {/* Cover */}
                <div className="flex justify-center md:justify-start">
                    {data.cover_url ? (
                        <img
                            src={data.cover_url}
                            alt={data.title}
                            data-testid="libro-cover"
                            className="w-[220px] md:w-full h-auto border border-[var(--border-strong)] shadow-2xl"
                        />
                    ) : (
                        <div className="w-[220px] md:w-full aspect-[2/3] bg-[var(--bg-elevated)] border border-[var(--border-strong)] flex items-center justify-center">
                            <BookOpen size={48} strokeWidth={1} className="text-[var(--accent)]" />
                        </div>
                    )}
                </div>

                {/* Detail */}
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">
                        {t("libro.eyebrow")}
                    </p>
                    <h1
                        data-testid="libro-title"
                        className="font-display text-4xl md:text-5xl text-[var(--ink)] italic leading-[1.1] tracking-tight mb-3"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 85, "opsz" 96, "slnt" -8' }}
                    >
                        {data.title}
                    </h1>
                    {data.author && (
                        <p className="text-lg text-[var(--ink-muted)] mb-1">
                            {t("libro.by")} <span className="text-[var(--ink)]">{data.author}</span>
                        </p>
                    )}
                    <p className="text-xs uppercase tracking-widest text-[var(--ink-dim)] mb-8">
                        {[data.year, data.pages && `${data.pages} ${t("libro.pages")}`, data.isbn]
                            .filter(Boolean)
                            .join(" · ")}
                    </p>

                    {data.synopsis && (
                        <p className="text-[var(--ink-muted)] leading-relaxed text-base mb-10 max-w-2xl">
                            {data.synopsis.length > 600
                                ? data.synopsis.slice(0, 600).trimEnd() + "…"
                                : data.synopsis}
                        </p>
                    )}

                    {/* Affiliate buttons grid — same layout as BookCard but
                        bigger / more prominent since this is the dedicated
                        page for one book. */}
                    <div className="border-t border-[var(--border)] pt-8">
                        <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--ink-muted)] mb-4 font-medium">
                            {t("libro.buy")}
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                            {chainStores.map((store) => {
                                const Icon = ICONS[store.icon] || ExternalLink;
                                const query = `${book.title} ${book.author || ""}`.trim();
                                return (
                                    <a
                                        key={store.id}
                                        data-testid={`libro-affil-${store.id}`}
                                        href={store.build(query, book.isbn)}
                                        target="_blank"
                                        rel="noopener noreferrer sponsored"
                                        onClick={() => {
                                            try {
                                                window.recomendalibros?.trackClick?.({
                                                    store: store.id,
                                                    book_title: book.title,
                                                    book_author: book.author,
                                                    isbn: book.isbn || null,
                                                    source: "libro_page",
                                                });
                                            } catch { /* ignore */ }
                                        }}
                                        className="flex items-center justify-center gap-2 border border-[var(--border-strong)] hover:border-[var(--ink)] hover:bg-[var(--bg-hover)] text-[var(--ink-muted)] hover:text-[var(--ink)] rounded py-3 px-3 text-sm transition-all uppercase tracking-wider"
                                    >
                                        <Icon size={14} strokeWidth={1.5} />
                                        <span>{store.name}</span>
                                    </a>
                                );
                            })}
                        </div>
                        {indieStores.map((store) => {
                            const Icon = ICONS[store.icon] || ExternalLink;
                            const query = `${book.title} ${book.author || ""}`.trim();
                            return (
                                <a
                                    key={store.id}
                                    data-testid={`libro-affil-${store.id}`}
                                    href={store.build(query, book.isbn)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => {
                                        try {
                                            window.recomendalibros?.trackClick?.({
                                                store: store.id,
                                                book_title: book.title,
                                                book_author: book.author,
                                                isbn: book.isbn || null,
                                                source: "libro_page",
                                            });
                                        } catch { /* ignore */ }
                                    }}
                                    className="flex items-center justify-center gap-2 border border-[var(--accent)]/40 hover:border-[var(--accent)] hover:bg-[var(--accent)]/10 text-[var(--accent)] rounded py-3 px-3 text-sm transition-all uppercase tracking-wider"
                                >
                                    <Icon size={14} strokeWidth={1.5} />
                                    <span>{store.name}</span>
                                </a>
                            );
                        })}
                    </div>

                    {/* CTA to fire the AI recommender. Optional — reader can
                        always just close the page, but this is the natural
                        next step after "I already own / have read this one". */}
                    <div className="mt-12 pt-8 border-t border-[var(--border)] flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        <button
                            type="button"
                            data-testid="libro-see-similar"
                            onClick={handleSeeSimilar}
                            className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] hover:scale-105 transition-all px-6 py-3 text-xs uppercase tracking-widest"
                        >
                            <Sparkles size={13} strokeWidth={1.6} />
                            {t("libro.see_similar", { title: data.title })}
                        </button>
                        <p className="text-xs text-[var(--ink-dim)] max-w-sm">
                            {t("libro.see_similar_note")}
                        </p>
                    </div>
                </div>
            </div>
        </main>
    );
}
